import './ResponseItem.scss';

import React, {
  memo,
  useMemo,
} from 'react';

import Loader from '../../../components/V2/Loader/Loader';
import { useTQConnectConfig } from '../../../hooks/queries/tqConnectQueries';
import {
  useTQConnectUserProfileInfo,
  useTQProfileCompletionQueries,
} from '../../../hooks/queries/tqConnectQueries/user';
import { useAppNavigate } from '../../../shared/hooks';
import { getTQProfileQueryAttemptStatus } from '../../../shared/tqConnectUtils';
import { QuestionAttemptStatus } from '../../../types/assignmentTypes';
import { ROUTES } from '../../../types/routes';
import {
  ITQProfileCompletionQueryWithResponse,
} from '../../../types/tqConnectTypes/user';
import {
  OnboardingStatus,
  UserAccountType,
} from '../../../types/userTypes';
import ResponseItem from './ResponseItem';

interface Props {
  userProfileId: string;
  userProfileType: OnboardingStatus;
  userProfilePicUrl: string;
  viewerType: UserAccountType;
  isViewerSelf?: boolean;
}

const ProfileCompletionResponses: React.FC<Props> = ({
  userProfileId,
  userProfileType,
  viewerType,
  isViewerSelf,
}) => {
  const navigate = useAppNavigate();
  const { data: tqConnectConfigResult, isPending: isLoadingTQConnectConfig } =
    useTQConnectConfig();

  const scoreName = tqConnectConfigResult?.tqConnectConfig?.scoreConfig?.name;

  const { data: tqConnectProfileCompletionQueries = [] } =
    useTQProfileCompletionQueries(true);

  const { data: tqConnectUserProfileInfo, isLoading } =
    useTQConnectUserProfileInfo(
      {
        userId: userProfileId,
        userType: userProfileType,
      },
      true,
    );

  const checkIfHasAnswer = (
    response: ITQProfileCompletionQueryWithResponse,
  ) => {
    return Boolean(
      response?.selectedOptions?.length || response?.subjectiveAnswer?.trim(),
    );
  };

  const tqProfileCompletionQueriesWithResponse = useMemo<
    ITQProfileCompletionQueryWithResponse[]
  >(() => {
    return (
      tqConnectProfileCompletionQueries
        ?.map((question) => {
          const answer =
            tqConnectUserProfileInfo?.tqConnectProfileCompletionResponses?.find(
              (q) => q.questionId === question._id,
            );
          return {
            ...question,
            questionId: question._id,
            question: question.question,
            order: question.order,
            selectedOptions: answer?.selectedOptions,
            subjectiveAnswer: answer?.subjectiveAnswer,
          };
        })
        // if viewer is self or creator, show all responses, otherwise show only responses with answer (while viewing other user's profile)
        .filter(
          (r) =>
            isViewerSelf || viewerType === 'creator' || checkIfHasAnswer(r),
        )
    );
  }, [
    tqConnectProfileCompletionQueries,
    tqConnectUserProfileInfo?.tqConnectProfileCompletionResponses,
    isViewerSelf,
    viewerType,
  ]);

  return (
    <>
      {isLoading || isLoadingTQConnectConfig ? (
        <Loader type="dots" />
      ) : (
        (tqProfileCompletionQueriesWithResponse.length && (
          <div className="responses__list">
            {tqProfileCompletionQueriesWithResponse.map((response) => {
              const hasAnswer = checkIfHasAnswer(response);

              return (
                <ResponseItem
                  key={response.questionId}
                  scoreName={scoreName || 'TQ'}
                  response={response}
                  hasAnswer={hasAnswer}
                  canAddAnswer={isViewerSelf && !hasAnswer}
                  onAddAnswer={() => {
                    navigate(
                      ROUTES.TQ_CONNECT_PROFILE_COMPLETE_QUESTIONS,
                      {},
                      { search: `initialQuestionNo=${response.order}` },
                    );
                  }}
                  isLastUnattempted={tqProfileCompletionQueriesWithResponse
                    .filter((q) => q._id !== response.questionId)
                    .every(
                      (q) =>
                        getTQProfileQueryAttemptStatus(q) ===
                        QuestionAttemptStatus.ATTEMPTED,
                    )}
                />
              );
            })}
          </div>
        )) ||
        null
      )}
    </>
  );
};

export default memo(ProfileCompletionResponses);
